import { type CountryCode } from "libphonenumber-js";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { useFormatPhone } from "@/hooks/useFormatPhone";
import { useCallback, useEffect, useMemo, useState } from "react";
import { CountryDropdown } from "@/components/shared/CountryDropdown";
import { type Nationality } from "@/types/nationality";
import { useNationalities } from "@/hooks/useNationalities";
import { Typography } from "@/components/Typography";
import { CircleFlag } from "react-circle-flags";

type PhoneFieldProps = {
  label?: string;
  placeholder?: string;
  value?: string;
  onChange?: (value: string) => void;
  countryCode?: CountryCode | string;
  onCountryCodeChange?: (country: Nationality) => void;
  nationalities?: Nationality[];
  disabled?: boolean;
  error?: boolean;
  errorMessage?: string;
  allowCountryChange?: boolean;
  inputClassName?: string;
  containerClassName?: string;
  trailing?: React.ReactNode;
} & Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "value" | "onChange" | "placeholder"
>;

const PhoneField = ({
  label = "Phone Number",
  placeholder = "Phone number",
  value,
  onChange,
  countryCode: countryCodeProp,
  onCountryCodeChange,
  nationalities: propNationalities,
  disabled = false,
  error = false,
  errorMessage,
  allowCountryChange = false,
  inputClassName,
  containerClassName,
  trailing,
  ...props
}: PhoneFieldProps) => {
  const { data: apiNationalities } = useNationalities();
  const nationalities = useMemo(
    () => propNationalities ?? apiNationalities ?? [],
    [propNationalities, apiNationalities],
  );
  const { formatPhone } = useFormatPhone();

  const [internalCountryCode, setInternalCountryCode] = useState<string>(
    typeof countryCodeProp === "string" && countryCodeProp
      ? countryCodeProp
      : "KH",
  );

  useEffect(() => {
    if (countryCodeProp) {
      setInternalCountryCode(countryCodeProp);
    }
  }, [countryCodeProp]);

  const activeCountryCode = allowCountryChange
    ? countryCodeProp || internalCountryCode
    : "KH";

  const currentIsoCode = useMemo(() => {
    if (!allowCountryChange) return "KH";
    if (!activeCountryCode) return "KH";
    if (
      activeCountryCode === "855" ||
      activeCountryCode === "+855" ||
      activeCountryCode.toUpperCase() === "KH"
    ) {
      return "KH";
    }
    if (activeCountryCode.length === 2) {
      return activeCountryCode.toUpperCase() as CountryCode;
    }
    const clean = activeCountryCode.replace("+", "").trim();
    const found = nationalities.find(
      (n) => n.isd?.replace("+", "").trim() === clean,
    );
    return (found?.code?.toUpperCase() as CountryCode) || "KH";
  }, [allowCountryChange, activeCountryCode, nationalities]);

  const handleCountryChange = useCallback(
    (country: Nationality) => {
      setInternalCountryCode(country.code);
      onCountryCodeChange?.(country);
      if (value) {
        const formatted = formatPhone(value, country.code as CountryCode);
        onChange?.(formatted);
      }
    },
    [formatPhone, onChange, onCountryCodeChange, value],
  );

  const handleChange = useCallback(
    (nextValue: string) => {
      if (disabled) {
        return;
      }
      const formattedValue = formatPhone(nextValue, currentIsoCode);
      onChange?.(formattedValue);
    },
    [disabled, currentIsoCode, formatPhone, onChange],
  );

  const isInvalid = Boolean(error || errorMessage);

  return (
    <div className="flex flex-col gap-1.5">
      {label ? (
        <label className="text-sm font-semibold text-[#4A4F55]">{label}</label>
      ) : null}
      <div
        className={cn(
          "flex w-full items-center rounded-xl border bg-white transition-colors focus-within:border-primary",
          disabled && "cursor-not-allowed opacity-60",
          isInvalid &&
            "border-error-foreground focus-within:border-error-foreground",
          containerClassName,
        )}
        aria-disabled={disabled}
      >
        {allowCountryChange ? (
          <CountryDropdown
            options={nationalities}
            value={activeCountryCode}
            onChange={handleCountryChange}
            variant="dialCode"
            disabled={disabled}
          />
        ) : (
          <div className="flex h-full items-center gap-1.5 select-none px-3">
            <CircleFlag
              countryCode="kh"
              height="20"
              width="20"
              className="shrink-0"
            />
            <span className="text-[16px] font-semibold text-body">+855</span>
          </div>
        )}

        <div className="h-6 w-px shrink-0 border-l" />

        <Input
          value={value}
          onChange={(event) => handleChange(event.target.value)}
          type="tel"
          disabled={disabled}
          placeholder={placeholder}
          className={cn(
            "h-[48px] min-w-0 flex-1 border-0 bg-transparent px-3 text-[16px] text-heading shadow-none focus-visible:ring-0 placeholder-subtle",
            inputClassName,
          )}
          {...props}
        />
        {trailing && <div className="shrink-0 pr-3">{trailing}</div>}
      </div>

      {errorMessage ? (
        <Typography variant="body2" className="text-error-foreground">
          {errorMessage}
        </Typography>
      ) : null}
    </div>
  );
};

export default PhoneField;
